import { supabase } from '../config/supabase';
import {
  Enrollment,
  EnrollmentWithDetails,
  EnrollmentStatus,
  CourseProgress
} from '../types';
import { learnersService } from './learners';

// Helpers to convert DB rows to types
const toEnrollment = (row: Record<string, unknown>): Enrollment => ({
  id: row.id as string,
  learnerId: row.learner_id as string,
  programId: row.program_id as string,
  enrolledAt: new Date(row.enrolled_at as string),
  dueDate: row.due_date ? new Date(row.due_date as string) : undefined,
  completedAt: row.completed_at ? new Date(row.completed_at as string) : undefined,
  progressPercent: (row.progress_percent as number) || 0,
  status: row.status as EnrollmentStatus
});

const toCourseProgress = (row: Record<string, unknown>): CourseProgress => ({
  id: row.id as string,
  enrollmentId: row.enrollment_id as string,
  courseId: row.course_id as string,
  progressPercent: (row.progress_percent as number) || 0,
  startedAt: row.started_at ? new Date(row.started_at as string) : undefined,
  completedAt: row.completed_at ? new Date(row.completed_at as string) : undefined,
  timeSpentMinutes: (row.time_spent_minutes as number) || 0,
  lastActivityAt: row.last_activity_at ? new Date(row.last_activity_at as string) : undefined,
  lmsSyncAt: row.lms_sync_at ? new Date(row.lms_sync_at as string) : undefined,
  course: row.course ? {
    id: (row.course as Record<string, unknown>).id as string,
    lmsCourseId: (row.course as Record<string, unknown>).lms_course_id as string,
    name: (row.course as Record<string, unknown>).name as string,
    description: (row.course as Record<string, unknown>).description as string | undefined,
    durationMinutes: (row.course as Record<string, unknown>).duration_minutes as number | undefined,
    createdAt: new Date((row.course as Record<string, unknown>).created_at as string)
  } : undefined
});

export const enrollmentsService = {
  // =====================================================
  // ENROLLMENTS
  // =====================================================

  // Get all enrollments for a learner
  async getByLearner(learnerId: string): Promise<EnrollmentWithDetails[]> {
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        program:training_programs(*),
        course_progress(*, course:courses(*))
      `)
      .eq('learner_id', learnerId)
      .order('enrolled_at', { ascending: false });

    if (error) {
      console.error('Error fetching enrollments:', error);
      throw new Error('Impossible de charger les inscriptions');
    }

    return (data || []).map((row) => ({
      ...toEnrollment(row),
      program: row.program ? {
        id: row.program.id,
        organizationId: row.program.organization_id,
        name: row.program.name,
        description: row.program.description,
        estimatedDurationHours: row.program.estimated_duration_hours,
        isActive: row.program.is_active,
        createdAt: new Date(row.program.created_at),
        updatedAt: new Date(row.program.updated_at)
      } : undefined,
      courseProgress: (row.course_progress || []).map(toCourseProgress)
    }));
  },

  // Get enrollments for a program
  async getByProgram(programId: string): Promise<Enrollment[]> {
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .eq('program_id', programId)
      .order('enrolled_at', { ascending: false });

    if (error) {
      console.error('Error fetching enrollments:', error);
      throw new Error('Impossible de charger les inscriptions');
    }

    return (data || []).map(toEnrollment);
  },

  // Get single enrollment by ID
  async getById(id: string): Promise<EnrollmentWithDetails | null> {
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        program:training_programs(*),
        course_progress(*, course:courses(*))
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('Error fetching enrollment:', error);
      throw new Error('Impossible de charger l\'inscription');
    }

    if (!data) return null;

    return {
      ...toEnrollment(data),
      program: data.program ? {
        id: data.program.id,
        organizationId: data.program.organization_id,
        name: data.program.name,
        description: data.program.description,
        estimatedDurationHours: data.program.estimated_duration_hours,
        isActive: data.program.is_active,
        createdAt: new Date(data.program.created_at),
        updatedAt: new Date(data.program.updated_at)
      } : undefined,
      courseProgress: (data.course_progress || []).map(toCourseProgress)
    };
  },

  // Create enrollment
  async create(enrollment: Omit<Enrollment, 'id' | 'progressPercent' | 'status'>): Promise<Enrollment> {
    const { data, error } = await supabase
      .from('enrollments')
      .insert({
        learner_id: enrollment.learnerId,
        program_id: enrollment.programId,
        enrolled_at: enrollment.enrolledAt.toISOString(),
        due_date: enrollment.dueDate?.toISOString(),
        status: 'not_started',
        progress_percent: 0
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating enrollment:', error);
      throw new Error('Impossible de créer l\'inscription');
    }

    // Initialize course progress for all courses in the program
    const { data: programCourses } = await supabase
      .from('program_courses')
      .select('course_id')
      .eq('program_id', enrollment.programId);

    if (programCourses && programCourses.length > 0) {
      const progressRecords = programCourses.map(pc => ({
        enrollment_id: data.id,
        course_id: pc.course_id,
        progress_percent: 0,
        time_spent_minutes: 0
      }));

      await supabase.from('course_progress').insert(progressRecords);
    }

    return toEnrollment(data);
  },

  // Update enrollment
  async update(id: string, updates: Partial<Omit<Enrollment, 'id' | 'learnerId' | 'programId' | 'enrolledAt'>>): Promise<Enrollment> {
    const updateData: Record<string, unknown> = {};

    if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate?.toISOString();
    if (updates.completedAt !== undefined) updateData.completed_at = updates.completedAt?.toISOString();
    if (updates.progressPercent !== undefined) updateData.progress_percent = updates.progressPercent;
    if (updates.status !== undefined) updateData.status = updates.status;

    const { data, error } = await supabase
      .from('enrollments')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating enrollment:', error);
      throw new Error('Impossible de mettre à jour l\'inscription');
    }

    // Update learner status if needed
    await learnersService.updateStatusFromEnrollments(data.learner_id);

    return toEnrollment(data);
  },

  // Delete enrollment
  async delete(id: string): Promise<void> {
    // Get learner ID before deleting
    const { data: enrollment } = await supabase
      .from('enrollments')
      .select('learner_id')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('enrollments')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting enrollment:', error);
      throw new Error('Impossible de supprimer l\'inscription');
    }

    // Update learner status
    if (enrollment) {
      await learnersService.updateStatusFromEnrollments(enrollment.learner_id);
    }
  },

  // Create a completed enrollment (for historical imports)
  async createCompleted(
    learnerId: string,
    programId: string,
    enrolledAt: Date,
    completedAt?: Date
  ): Promise<Enrollment> {
    const { data, error } = await supabase
      .from('enrollments')
      .insert({
        learner_id: learnerId,
        program_id: programId,
        enrolled_at: enrolledAt.toISOString(),
        completed_at: (completedAt || enrolledAt).toISOString(),
        status: 'completed',
        progress_percent: 100
      })
      .select()
      .single();

    if (error) {
      // Ignore duplicate enrollments
      if (error.code === '23505') {
        console.log('Enrollment already exists, skipping');
        const { data: existing } = await supabase
          .from('enrollments')
          .select('*')
          .eq('learner_id', learnerId)
          .eq('program_id', programId)
          .single();
        if (existing) return toEnrollment(existing);
      }
      console.error('Error creating completed enrollment:', error);
      throw new Error('Impossible de créer l\'inscription');
    }

    // Update learner status
    await learnersService.updateStatusFromEnrollments(learnerId);

    return toEnrollment(data);
  },

  // Enroll multiple learners in a program
  async bulkEnroll(learnerIds: string[], programId: string, dueDate?: Date): Promise<Enrollment[]> {
    const now = new Date();
    const insertData = learnerIds.map(learnerId => ({
      learner_id: learnerId,
      program_id: programId,
      enrolled_at: now.toISOString(),
      due_date: dueDate?.toISOString(),
      status: 'not_started',
      progress_percent: 0
    }));

    const { data, error } = await supabase
      .from('enrollments')
      .insert(insertData)
      .select();

    if (error) {
      console.error('Error bulk enrolling:', error);
      throw new Error('Impossible d\'inscrire les apprenants');
    }

    // Initialize course progress for each enrollment
    const { data: programCourses } = await supabase
      .from('program_courses')
      .select('course_id')
      .eq('program_id', programId);

    if (programCourses && programCourses.length > 0 && data) {
      const progressRecords = data.flatMap(enrollment =>
        programCourses.map(pc => ({
          enrollment_id: enrollment.id,
          course_id: pc.course_id,
          progress_percent: 0,
          time_spent_minutes: 0
        }))
      );

      await supabase.from('course_progress').insert(progressRecords);
    }

    return (data || []).map(toEnrollment);
  },

  // =====================================================
  // COURSE PROGRESS
  // =====================================================

  // Get progress for an enrollment
  async getCourseProgress(enrollmentId: string): Promise<CourseProgress[]> {
    const { data, error } = await supabase
      .from('course_progress')
      .select(`
        *,
        course:courses(*)
      `)
      .eq('enrollment_id', enrollmentId)
      .order('course_id');

    if (error) {
      console.error('Error fetching course progress:', error);
      throw new Error('Impossible de charger la progression');
    }

    return (data || []).map(toCourseProgress);
  },

  // Update course progress (called from LMS webhook)
  async updateCourseProgress(
    enrollmentId: string,
    courseId: string,
    updates: {
      progressPercent?: number;
      timeSpentMinutes?: number;
      completedAt?: Date;
    }
  ): Promise<CourseProgress> {
    const updateData: Record<string, unknown> = {
      last_activity_at: new Date().toISOString(),
      lms_sync_at: new Date().toISOString()
    };

    if (updates.progressPercent !== undefined) {
      updateData.progress_percent = updates.progressPercent;
      if (updates.progressPercent > 0 && !updateData.started_at) {
        updateData.started_at = new Date().toISOString();
      }
    }
    if (updates.timeSpentMinutes !== undefined) updateData.time_spent_minutes = updates.timeSpentMinutes;
    if (updates.completedAt !== undefined) updateData.completed_at = updates.completedAt.toISOString();

    const { data, error } = await supabase
      .from('course_progress')
      .update(updateData)
      .eq('enrollment_id', enrollmentId)
      .eq('course_id', courseId)
      .select(`
        *,
        course:courses(*)
      `)
      .single();

    if (error) {
      console.error('Error updating course progress:', error);
      throw new Error('Impossible de mettre à jour la progression');
    }

    // Recalculate enrollment progress
    await this.recalculateEnrollmentProgress(enrollmentId);

    return toCourseProgress(data);
  },

  // Recalculate enrollment overall progress from course progress
  async recalculateEnrollmentProgress(enrollmentId: string): Promise<void> {
    // Get all course progress for this enrollment
    const { data: progressRecords, error } = await supabase
      .from('course_progress')
      .select('progress_percent, completed_at')
      .eq('enrollment_id', enrollmentId);

    if (error || !progressRecords || progressRecords.length === 0) {
      return;
    }

    // Calculate average progress
    const totalProgress = progressRecords.reduce((sum, p) => sum + (p.progress_percent || 0), 0);
    const averageProgress = Math.round(totalProgress / progressRecords.length);

    // Determine status
    const allCompleted = progressRecords.every(p => p.progress_percent === 100);
    const anyStarted = progressRecords.some(p => p.progress_percent > 0);

    let status: EnrollmentStatus = 'not_started';
    let completedAt: string | null = null;

    if (allCompleted) {
      status = 'completed';
      completedAt = new Date().toISOString();
    } else if (anyStarted) {
      status = 'in_progress';
    }

    // Check if overdue
    const { data: enrollment } = await supabase
      .from('enrollments')
      .select('due_date, learner_id')
      .eq('id', enrollmentId)
      .single();

    if (enrollment?.due_date && new Date(enrollment.due_date) < new Date() && status !== 'completed') {
      status = 'overdue';
    }

    // Update enrollment
    await supabase
      .from('enrollments')
      .update({
        progress_percent: averageProgress,
        status,
        completed_at: completedAt
      })
      .eq('id', enrollmentId);

    // Update learner status
    if (enrollment) {
      await learnersService.updateStatusFromEnrollments(enrollment.learner_id);
    }
  },

  // Sync progress from LMS (webhook handler)
  async syncFromLMS(
    lmsUserId: string,
    lmsCourseId: string,
    progressPercent: number,
    timeSpentMinutes?: number,
    completedAt?: Date
  ): Promise<void> {
    // Find learner by LMS user ID
    const { data: learner } = await supabase
      .from('learners')
      .select('id')
      .eq('lms_user_id', lmsUserId)
      .single();

    if (!learner) {
      console.warn(`Learner not found for LMS user: ${lmsUserId}`);
      return;
    }

    // Find course by LMS course ID
    const { data: course } = await supabase
      .from('courses')
      .select('id')
      .eq('lms_course_id', lmsCourseId)
      .single();

    if (!course) {
      console.warn(`Course not found for LMS course: ${lmsCourseId}`);
      return;
    }

    // Find enrollment containing this course
    const { data: courseProgress } = await supabase
      .from('course_progress')
      .select('enrollment_id, enrollment:enrollments!inner(learner_id)')
      .eq('course_id', course.id)
      .eq('enrollment.learner_id', learner.id)
      .single();

    if (!courseProgress) {
      console.warn(`No enrollment found for learner ${lmsUserId} and course ${lmsCourseId}`);
      return;
    }

    // Update progress
    await this.updateCourseProgress(courseProgress.enrollment_id, course.id, {
      progressPercent,
      timeSpentMinutes,
      completedAt
    });
  },

  // =====================================================
  // STATISTICS
  // =====================================================

  // Get enrollment stats for an organization
  async getOrganizationStats(organizationId: string): Promise<{
    totalEnrollments: number;
    completedEnrollments: number;
    inProgressEnrollments: number;
    overdueEnrollments: number;
    averageProgress: number;
    averageCompletionTimeHours: number | null;
  }> {
    // Get all enrollments for learners in this organization
    const { data: enrollments, error } = await supabase
      .from('enrollments')
      .select(`
        status,
        progress_percent,
        enrolled_at,
        completed_at,
        learner:learners!inner(organization_id)
      `)
      .eq('learner.organization_id', organizationId);

    if (error) {
      console.error('Error fetching enrollment stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const data = enrollments || [];
    const total = data.length;
    const completed = data.filter(e => e.status === 'completed').length;
    const inProgress = data.filter(e => e.status === 'in_progress').length;
    const overdue = data.filter(e => e.status === 'overdue').length;
    const avgProgress = total > 0
      ? Math.round(data.reduce((sum, e) => sum + (e.progress_percent || 0), 0) / total)
      : 0;

    // Calculate average completion time
    const completedEnrollments = data.filter(e => e.status === 'completed' && e.completed_at);
    let avgCompletionTime: number | null = null;

    if (completedEnrollments.length > 0) {
      const totalHours = completedEnrollments.reduce((sum, e) => {
        const start = new Date(e.enrolled_at);
        const end = new Date(e.completed_at);
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours;
      }, 0);
      avgCompletionTime = Math.round(totalHours / completedEnrollments.length);
    }

    return {
      totalEnrollments: total,
      completedEnrollments: completed,
      inProgressEnrollments: inProgress,
      overdueEnrollments: overdue,
      averageProgress: avgProgress,
      averageCompletionTimeHours: avgCompletionTime
    };
  },

  // Get program stats
  async getProgramStats(programId: string): Promise<{
    totalEnrollments: number;
    completedEnrollments: number;
    averageProgress: number;
    completionRate: number;
  }> {
    const { data, error } = await supabase
      .from('enrollments')
      .select('status, progress_percent')
      .eq('program_id', programId);

    if (error) {
      console.error('Error fetching program stats:', error);
      throw new Error('Impossible de charger les statistiques');
    }

    const enrollments = data || [];
    const total = enrollments.length;
    const completed = enrollments.filter(e => e.status === 'completed').length;
    const avgProgress = total > 0
      ? Math.round(enrollments.reduce((sum, e) => sum + (e.progress_percent || 0), 0) / total)
      : 0;

    return {
      totalEnrollments: total,
      completedEnrollments: completed,
      averageProgress: avgProgress,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  },

  // Get group stats for a program (enrollments grouped by learner department)
  async getProgramGroupStats(programId: string): Promise<{
    groupName: string;
    totalEnrollments: number;
    completedEnrollments: number;
    lastCompletedAt: Date | null;
  }[]> {
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        status,
        completed_at,
        learner:learners!inner(department)
      `)
      .eq('program_id', programId);

    if (error) {
      console.error('Error fetching program group stats:', error);
      throw new Error('Impossible de charger les statistiques par groupe');
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Group by department
    const groupMap = new Map<string, { total: number; completed: number; lastCompletedAt: Date | null }>();

    data.forEach((enrollment) => {
      const department = (enrollment.learner as { department?: string })?.department || 'Sans groupe';

      if (!groupMap.has(department)) {
        groupMap.set(department, { total: 0, completed: 0, lastCompletedAt: null });
      }

      const stats = groupMap.get(department)!;
      stats.total++;
      if (enrollment.status === 'completed') {
        stats.completed++;
        // Track most recent completion date
        if (enrollment.completed_at) {
          const completedDate = new Date(enrollment.completed_at as string);
          if (!stats.lastCompletedAt || completedDate > stats.lastCompletedAt) {
            stats.lastCompletedAt = completedDate;
          }
        }
      }
    });

    // Convert to array and sort by most recent completion date (descending)
    return Array.from(groupMap.entries())
      .map(([groupName, stats]) => ({
        groupName,
        totalEnrollments: stats.total,
        completedEnrollments: stats.completed,
        lastCompletedAt: stats.lastCompletedAt
      }))
      .sort((a, b) => {
        // Groups with completions first, sorted by most recent
        if (a.lastCompletedAt && b.lastCompletedAt) {
          return b.lastCompletedAt.getTime() - a.lastCompletedAt.getTime();
        }
        if (a.lastCompletedAt) return -1;
        if (b.lastCompletedAt) return 1;
        // Groups without completions: sort alphabetically
        return a.groupName.localeCompare(b.groupName);
      });
  }
};
